import React from "react";
import { MapPin, Users, Clock } from "lucide-react";
import { DartsEvent, ThemeType } from "../../types";
import { getThemeColors } from "../../utils/theme";
import { UI_TEXT } from "../../constants/uiText";

interface PremiumEventCardProps {
  event: DartsEvent;
  isPrimary?: boolean;
  isPast?: boolean;
  theme: ThemeType;
  onClick: (event: DartsEvent) => void;
}

const MONTH_NAMES = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];
const DAY_OF_WEEK_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const PremiumEventCard: React.FC<PremiumEventCardProps> = ({
  event,
  isPrimary,
  isPast,
  theme,
  onClick,
}) => {
  const colors = getThemeColors(theme);
  const dateParts = event.date.split(" / ");
  const month = MONTH_NAMES[parseInt(dateParts[1], 10) - 1] || "MTH";
  const dayAndTime = dateParts[2] ? dateParts[2].split(" ") : ["00", ""];
  const day = dayAndTime[0];
  const time = dayAndTime[1] || "";
  const dateTimeObj = new Date(
    `${dateParts[0]}-${dateParts[1]}-${day}T${time || "00:00"}`,
  );
  const dayOfWeek = DAY_OF_WEEK_NAMES[dateTimeObj.getDay()] || "DAY";

  return (
    <div
      onClick={() => onClick(event)}
      className={`relative rounded-[14px] p-2 mb-3 flex items-stretch group transition-all duration-300 active:scale-[0.98] cursor-pointer text-left ${
        isPast
          ? "bg-slate-50 shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-200 border-l-[3px] border-l-slate-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:bg-slate-100"
          : isPrimary
            ? `bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 border-l-[3px] ${colors.borderAccentStrong} hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:border-slate-200`
            : `bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 border-l-[3px] ${colors.borderAccent} hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:border-slate-200`
      }`}
    >
      <div
        className={`flex flex-col items-center justify-center min-w-[64px] rounded-xl ${
          isPrimary
            ? `${colors.bgSoft} ${colors.border}`
            : isPast
              ? "bg-slate-100 border border-slate-200"
              : "bg-slate-50 border border-slate-100"
        } py-1.5 px-1 relative overflow-hidden transition-colors`}
      >
        <span
          className={`text-[9px] font-black tracking-widest uppercase mb-0.5 z-10 ${isPrimary ? colors.textMain : isPast ? "text-slate-400" : "text-slate-500"}`}
        >
          {month}
        </span>
        <span
          className={`text-2xl font-black tracking-tighter leading-none my-0.5 z-10 ${isPrimary ? "text-slate-800" : isPast ? "text-slate-400" : "text-slate-700"}`}
        >
          {day}
        </span>
        <span
          className={`text-[8px] font-bold uppercase mt-0.5 z-10 ${isPrimary ? colors.textMain : isPast ? "text-slate-300" : "text-slate-400"}`}
        >
          {dayOfWeek}
        </span>
      </div>
      <div className="ml-3 flex-1 flex flex-col justify-center py-0.5 overflow-hidden">
        <div className="flex flex-col items-start gap-0.5 mb-1.5">
          {event.tournament && (
            <span
              className={`text-[9px] font-bold uppercase tracking-widest ${
                isPast ? "text-slate-400" : colors.textMain
              }`}
            >
              {event.tournament}
            </span>
          )}
          <h3
            className={`w-full font-bold text-[15px] leading-snug transition-colors whitespace-normal break-words ${
              isPast
                ? "text-slate-600 group-hover:text-slate-900"
                : `text-slate-800 group-hover:${colors.textMain}`
            }`}
          >
            {event.title}
          </h3>
          {event.status === "finished" ? (
            <span className="inline-flex items-center text-[8px] font-black bg-slate-400 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              {UI_TEXT.eventCard.status.finished}
            </span>
          ) : event.status === "in_progress" ? (
            <span className="inline-flex items-center text-[8px] font-black bg-amber-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              {UI_TEXT.eventCard.status.inProgress}
            </span>
          ) : event.entryStatus === "entered" ? (
            <span className="inline-flex items-center text-[8px] font-black bg-emerald-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              {UI_TEXT.eventCard.status.entered}
            </span>
          ) : (
            <span className="inline-flex items-center text-[8px] font-black bg-blue-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider">
              {UI_TEXT.eventCard.status.open}
            </span>
          )}
          {event.isExciting && (
            <span className="inline-flex items-center text-[8px] font-black bg-slate-800 text-rose-400 px-1.5 py-0.5 rounded uppercase tracking-wider">
              {UI_TEXT.eventCard.exciting}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {time && (
            <div className="flex items-center text-[10px] font-bold text-slate-500 bg-slate-100/80 px-2 py-1 rounded-md tracking-wide">
              <Clock className="w-3 h-3 mr-1" />
              <span>{time}</span>
            </div>
          )}
          <div className="flex items-center text-[10px] font-bold text-slate-500 bg-slate-100/80 px-2 py-1 rounded-md tracking-wide">
            <MapPin className="w-3 h-3 mr-1" />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center text-[10px] font-bold text-slate-500 bg-slate-100/80 px-2 py-1 rounded-md tracking-wide">
            <Users className="w-3 h-3 mr-1" />
            {event.participantCount}
            {UI_TEXT.eventCard.participantUnit}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumEventCard;
